import { RefObject, useLayoutEffect, useMemo, useState } from 'react'

import { debounce } from '@utils/debounce'

export function useTextOverflow(elementRef: RefObject<HTMLElement | null>) {
  const [overflowing, setOverflowing] = useState(false)

  const debouncedCheck = useMemo(
    () =>
      debounce(() => {
        const el = elementRef.current
        if (!el) return

        const range = document.createRange()
        range.selectNodeContents(el)
        const textWidth = range.getBoundingClientRect().width
        const containerWidth = el.getBoundingClientRect().width
        const isOverflowing = textWidth > containerWidth + 0.1

        setOverflowing((prev) => (prev === isOverflowing ? prev : isOverflowing))
      }, 100),
    [elementRef]
  )

  useLayoutEffect(() => {
    const el = elementRef.current
    if (!el) return

    const resizeObserver = new ResizeObserver(debouncedCheck)
    resizeObserver.observe(el)
    if (el.parentElement) resizeObserver.observe(el.parentElement)

    debouncedCheck()

    return () => {
      resizeObserver.disconnect()
      debouncedCheck.cancel()
    }
  }, [elementRef, debouncedCheck])

  return overflowing
}
